const CSRF_HEADER = 'X-CSRF-Token';
const MUTATING_METHODS = new Set(['POST', 'PUT', 'DELETE', 'PATCH']);

export function getCsrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}

export function ensureFormCsrfToken(form: HTMLFormElement): void {
    if (!form.querySelector('input[name="csrf_token"]')) {
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'csrf_token';
        csrfInput.value = getCsrfToken();
        form.appendChild(csrfInput);
    }
}

export function submitFormWithCsrf(form: HTMLFormElement | null): void {
    if (!form) {
        return;
    }

    ensureFormCsrfToken(form);
    // Recipient fields allow display names and multiple addresses (e.g.
    // "Jane Doe" <jane@example.com>; cc@example.com) which fail HTML5
    // type=email validation. Server-side Zend validators handle format checks.
    form.noValidate = true;
    if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
        return;
    }

    form.submit();
}

export function setupFetchCsrf(): void {
    if (window.__csrfFetchPatched) {
        return;
    }

    window.__csrfFetchPatched = true;
    const originalFetch = window.fetch.bind(window);

    window.fetch = function fetchWithCsrf(
        input: RequestInfo | URL,
        init?: RequestInit,
    ): Promise<Response> {
        const token = getCsrfToken();
        if (!token) {
            return originalFetch(input, init);
        }

        if (input instanceof Request) {
            const method = input.method.toUpperCase();
            if (!MUTATING_METHODS.has(method) || input.headers.has(CSRF_HEADER)) {
                return originalFetch(input, init);
            }

            const headers = new Headers(input.headers);
            headers.set(CSRF_HEADER, token);
            return originalFetch(new Request(input, {headers}));
        }

        const method = (init?.method ?? 'GET').toUpperCase();
        if (!MUTATING_METHODS.has(method)) {
            return originalFetch(input, init);
        }

        const headers = new Headers(init?.headers);
        if (!headers.has(CSRF_HEADER)) {
            headers.set(CSRF_HEADER, token);
        }

        return originalFetch(input, {...init, headers});
    };
}
